import {ITooltipOptions, ITooltipStyling} from "./tooltip.service.interface";
import {TooltipService} from "./tooltip.service";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {Check} from "@sparkware/app-utils";
import {createPopper} from "@popperjs/core";
import {flipTooltipModifier, offsetTooltipModifier, preventOverflowTooltipModifier} from "./tooltip.modifiers";
import {v4 as uuidv4} from "uuid";
import {namedSetTimeout} from "../../../common/utils/time-functions";
import {ITooltipViewModel} from "./tooltip-view-model.interface";
import React from "react";
import {DomEventSubscription} from "../../../utils/event-listener-registration";
import {action, observable} from "mobx";

export class TooltipModel implements ITooltipViewModel {
    constructor(protected readonly tooltipService: TooltipService,
                protected readonly services: IServiceFactoryExtended,
                protected readonly options: ITooltipOptions) {
        this.id = uuidv4();
    }

    private _onDocumentClickHandler = (event: any) => {
        if(this.options.closeOnClickOutside && !this.tooltipContainer.contains(event.target)) {
            this.closeTooltip();
        }
    }

    readonly id: string;

    private _domEventSubscription: DomEventSubscription[] = [];

    private _autoCloseTimeout: any = null;
    private _popperInstance: any = null;

    @observable.ref
    tooltipContainer: HTMLElement;

    get display(): boolean {
        return true;
    }

    dispose(): void {
    }

    @action.bound
    onToolTipRendered(tooltipContainer: HTMLElement): void {

        this.tooltipContainer = tooltipContainer;

        if(this.options.onTooltipRendered) {
            this.options.onTooltipRendered();
        }
    }

    private _getElementToAttach(): Element {
        if(!this.options.attachTo) {
            return this.services.dom.body;
        }

        if(Check.isString(this.options.attachTo)) {
            return this.services.dom.querySelector(this.options.attachTo);
        }

        return this.options.attachTo;
    }

    get zIndex(): number {
        if(Check.isNullOrUndefined(this.options.zIndex)) {
            return 203;
        }

        if(this.options.showOverlay) {
            return this.options.zIndex + 1;
        }

        return this.options.zIndex;
    }

    get animated(): boolean {
        if(Check.isNullOrUndefined(this.options.animated)) {
            return true;
        }

        return this.options.animated;
    }

    get hasGlow(): boolean {
        if(Check.isNullOrUndefined(this.options.hasGlow)) {
            return true;
        }

        return this.options.hasGlow;
    }

    get shouldShowCloseButton(): boolean {
        return Boolean(this.options.showCloseButton);
    }

    get invertCloseButtonColors(): boolean {
        return Boolean(this.options.invertCloseButtonColors);
    }


    get styling(): ITooltipStyling | undefined {
        return this.options.styling;
    }

    get showOverlay(): boolean {
        return Boolean(this.options.showOverlay);
    }

    private _createTooltipPopper() {
        const elementToAttach = this._getElementToAttach();
        return createPopper(elementToAttach, this.tooltipContainer, {
            placement: this.options.placement || 'auto',
            strategy: "fixed",
            modifiers: [
                preventOverflowTooltipModifier,
                flipTooltipModifier,
                offsetTooltipModifier,
                ...(this.options.modifiers || [])
            ]
        });
    }

    render(): string | React.ReactElement | null {
        if(!this.tooltipContainer) {
            return null;
        }

        if(!this._popperInstance) {
            this._popperInstance = this._createTooltipPopper()

            if(!Check.isNullOrUndefined(this.options.timeout)) {
                this._autoCloseTimeout = namedSetTimeout('TOOLTIP_CLOSE_TIMER', () => this.closeTooltip(), this.options.timeout.totalMilliseconds);
            }

            if(this.options.closeOnClickOutside) {
                // When a tool tip is created inside a click handler of a button
                // then this render method will be called inside the same click event handler.
                // If we subscribe immediately to the document click event then this event that we are inside will propagate to the document level
                // so our callback will be called and basically we close the tooltip before even show it.
                // So this is why we have this setTimeout here
                setTimeout(() => {
                    this._domEventSubscription.push(this.services.dom.subscribeTo.click(this._onDocumentClickHandler));
                });
            }
        }

        return this.options.content;

    }

    closeTooltip(): void {
        if (this._popperInstance) {
            this._popperInstance.destroy();
        }

        if(this._autoCloseTimeout) {
            clearTimeout(this._autoCloseTimeout);
        }

        this._domEventSubscription.forEach(ev => ev.dispose());
        this._domEventSubscription = [];

        this.tooltipService.removeTooltip(this);

        if(this.options.onTooltipClosed) {
            this.options.onTooltipClosed();
        }
    }
}



